import { ERROR_CODES, type ErrorCode } from '@adco-rooms/shared';
import { env } from '@/lib/env';

/**
 * Typed `fetch` wrapper for the ADCO Rooms API.
 *
 * Responsibilities:
 *  - attach the bearer token provided by the auth layer (never read from storage)
 *  - generate an `X-Correlation-Id` per request so UI errors can be traced
 *  - send an optional `Idempotency-Key` on critical writes
 *  - unwrap the `{ data, meta }` envelope
 *  - normalize `{ error: { code, message, details } }` into `ApiError`
 */

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];

export interface ApiRequestOptions {
  method?: HttpMethod;
  /** Serialized as JSON unless it is a `FormData` instance. */
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Sent as `Idempotency-Key`; required by the API for critical writes. */
  idempotencyKey?: string;
  /** Skips the Authorization header (health checks, public endpoints). */
  skipAuth?: boolean;
}

export interface ApiMeta {
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
  [key: string]: unknown;
}

export interface ApiResult<T> {
  data: T;
  meta?: ApiMeta;
}

/** Error thrown for every non-2xx response and for network failures. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: unknown;
  readonly correlationId: string | undefined;

  constructor(params: {
    code: ErrorCode;
    message: string;
    status: number;
    details?: unknown;
    correlationId?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details;
    this.correlationId = params.correlationId;
  }

  /** Network/CORS failure: the request never reached the API. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === ERROR_CODES.UNAUTHENTICATED;
  }

  get isForbidden(): boolean {
    return this.status === 403 || this.code === ERROR_CODES.FORBIDDEN;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  /** 4xx responses are caller mistakes; retrying them is pointless. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Extracts a user-facing message from anything thrown by the data layer. */
export function getErrorMessage(error: unknown, fallback = 'Ocurrió un error inesperado.'): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/* -------------------------------------------------------------------------- */
/* Token provider                                                              */
/* -------------------------------------------------------------------------- */

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider | null = null;

/**
 * Registered by the auth provider on mount. Keeping the token behind a callback
 * (instead of a module variable) guarantees it is never persisted and always
 * refreshed right before the request leaves.
 */
export function setAuthTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

export function createCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers / jsdom without randomUUID.
  return `cid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Convenience alias for callers that need an idempotency key up front. */
export const createIdempotencyKey = createCorrelationId;

function buildQueryString(query: Record<string, QueryValue> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
      continue;
    }
    params.append(key, String(value));
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${env.apiBaseUrl}${normalized}${buildQueryString(query)}`;
}

function isErrorEnvelope(payload: unknown): payload is {
  error: { code?: string; message?: string; details?: unknown; correlationId?: string };
} {
  return typeof payload === 'object' && payload !== null && 'error' in payload;
}

function isSuccessEnvelope(payload: unknown): payload is { data: unknown; meta?: ApiMeta } {
  return typeof payload === 'object' && payload !== null && 'data' in payload;
}

function toErrorCode(value: string | undefined, status: number): ErrorCode {
  if (value && value in ERROR_CODES) {
    return ERROR_CODES[value as keyof typeof ERROR_CODES];
  }
  if (status === 401) return ERROR_CODES.UNAUTHENTICATED;
  if (status === 403) return ERROR_CODES.FORBIDDEN;
  if (status === 404) return ERROR_CODES.NOT_FOUND;
  if (status === 409) return ERROR_CODES.CONFLICT;
  if (status === 422) return ERROR_CODES.VALIDATION_ERROR;
  if (status === 429) return ERROR_CODES.RATE_LIMITED;
  return ERROR_CODES.INTERNAL_ERROR;
}

const DEFAULT_MESSAGES: Partial<Record<ErrorCode, string>> = {
  [ERROR_CODES.UNAUTHENTICATED]: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  [ERROR_CODES.FORBIDDEN]: 'No tienes permisos para realizar esta acción.',
  [ERROR_CODES.NOT_FOUND]: 'No encontramos el recurso solicitado.',
  [ERROR_CODES.RESERVATION_OVERLAP]: 'La sala ya está reservada en ese horario.',
  [ERROR_CODES.GRAPH_UNAVAILABLE]: 'Microsoft 365 no está disponible en este momento.',
  [ERROR_CODES.RATE_LIMITED]: 'Demasiadas solicitudes. Espera unos segundos e inténtalo de nuevo.',
  [ERROR_CODES.INTERNAL_ERROR]: 'Ocurrió un error inesperado. Inténtalo de nuevo.',
};

/* -------------------------------------------------------------------------- */
/* Core request                                                                */
/* -------------------------------------------------------------------------- */

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResult<T>> {
  const { method = 'GET', body, query, signal, headers = {}, idempotencyKey, skipAuth } = options;

  const correlationId = createCorrelationId();
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-Id': correlationId,
    ...headers,
  };

  if (body !== undefined && !isFormData) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  if (idempotencyKey && method !== 'GET') {
    requestHeaders['Idempotency-Key'] = idempotencyKey;
  }

  if (!skipAuth && tokenProvider) {
    const token = await tokenProvider();
    if (token) requestHeaders.Authorization = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'omit',
  };
  if (signal) init.signal = signal;
  if (body !== undefined) {
    init.body = isFormData ? (body as FormData) : JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), init);
  } catch (cause) {
    // Abort is a legitimate cancellation, not a failure to report.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      code: ERROR_CODES.INTERNAL_ERROR,
      message: 'No pudimos conectarnos con el servidor. Revisa tu conexión.',
      status: 0,
      details: cause,
      correlationId,
    });
  }

  if (response.status === 204) {
    return { data: undefined as T };
  }

  const rawText = await response.text();
  let payload: unknown = null;
  if (rawText) {
    try {
      payload = JSON.parse(rawText) as unknown;
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const envelope = isErrorEnvelope(payload) ? payload.error : undefined;
    const code = toErrorCode(envelope?.code, response.status);
    throw new ApiError({
      code,
      message: envelope?.message ?? DEFAULT_MESSAGES[code] ?? `Error ${response.status}`,
      status: response.status,
      details: envelope?.details,
      correlationId: envelope?.correlationId ?? response.headers.get('x-correlation-id') ?? correlationId,
    });
  }

  if (isSuccessEnvelope(payload)) {
    const result: ApiResult<T> = { data: payload.data as T };
    if (payload.meta) result.meta = payload.meta;
    return result;
  }

  // Endpoints outside the envelope convention (e.g. /health) still resolve.
  return { data: payload as T };
}

/* -------------------------------------------------------------------------- */
/* Sugar                                                                       */
/* -------------------------------------------------------------------------- */

type BodylessOptions = Omit<ApiRequestOptions, 'method' | 'body'>;
type BodyOptions = Omit<ApiRequestOptions, 'method'>;

export const api = {
  /** Returns the unwrapped `data`. */
  async get<T>(path: string, options?: BodylessOptions): Promise<T> {
    const { data } = await apiRequest<T>(path, { ...options, method: 'GET' });
    return data;
  },

  /** Returns `{ data, meta }` — use for paginated admin lists. */
  getWithMeta<T>(path: string, options?: BodylessOptions): Promise<ApiResult<T>> {
    return apiRequest<T>(path, { ...options, method: 'GET' });
  },

  async post<T>(path: string, body?: unknown, options?: BodyOptions): Promise<T> {
    const { data } = await apiRequest<T>(path, { ...options, method: 'POST', body });
    return data;
  },

  async patch<T>(path: string, body?: unknown, options?: BodyOptions): Promise<T> {
    const { data } = await apiRequest<T>(path, { ...options, method: 'PATCH', body });
    return data;
  },

  async put<T>(path: string, body?: unknown, options?: BodyOptions): Promise<T> {
    const { data } = await apiRequest<T>(path, { ...options, method: 'PUT', body });
    return data;
  },

  async delete<T>(path: string, options?: BodyOptions): Promise<T> {
    const { data } = await apiRequest<T>(path, { ...options, method: 'DELETE' });
    return data;
  },
} as const;
